<template>
  <div class="wizard-fullpage wizard-onboarding" role="region" aria-labelledby="wizard-heading">
    <div class="wizard-main">
      <nav class="wizard-stepper" aria-label="Resume sections">
        <div class="wizard-stepper-header">
          <h2 id="wizard-heading" class="wizard-stepper-title">{{ stepperTitle }}</h2>
          <p class="wizard-stepper-progress">Step {{ currentStepIndex + 1 }} of {{ steps.length }}</p>
        </div>
        <div class="wizard-stepper-mobile" aria-label="Progress">
          <div
            class="wizard-mobile-progress"
            role="progressbar"
            :aria-valuenow="currentStepIndex + 1"
            aria-valuemin="1"
            :aria-valuemax="steps.length"
          >
            <div class="wizard-mobile-progress-fill" :style="{ width: mobileProgressPercent + '%' }"></div>
          </div>
          <p class="wizard-mobile-current-step">{{ steps[currentStepIndex].label }}</p>
          <div class="wizard-mobile-dots">
            <button
              v-for="(step, index) in steps"
              :key="'dot-' + step.id"
              type="button"
              class="wizard-mobile-dot"
              :class="{
                'is-complete': stepComplete(step),
                'is-current': currentStepIndex === index,
              }"
              :title="step.label"
              :aria-label="`Go to step ${index + 1}: ${step.label}`"
              :aria-current="currentStepIndex === index ? 'step' : undefined"
              @click="goToStep(index)"
            >
              <i v-if="stepComplete(step)" class="fa fa-check" aria-hidden="true"></i>
              <span v-else>{{ index + 1 }}</span>
            </button>
          </div>
        </div>
        <div class="wizard-stepper-steps wizard-stepper-steps-desktop">
          <button
            v-for="(step, index) in steps"
            :key="step.id"
            type="button"
            class="wizard-stepper-item"
            :class="{
              'is-complete': stepComplete(step),
              'is-current': currentStepIndex === index,
              'is-pending': !stepComplete(step) && currentStepIndex !== index,
            }"
            @click="goToStep(index)"
          >
            <span class="wizard-stepper-icon" v-if="stepComplete(step)">
              <i class="fa fa-check"></i>
            </span>
            <span class="wizard-stepper-number" v-else>{{ index + 1 }}</span>
            <span class="wizard-stepper-label">{{ step.label }}</span>
          </button>
        </div>
        <p v-if="wizardValidated" class="wizard-stepper-helper wizard-stepper-helper-desktop">
          You can come back anytime to edit your base resume.
        </p>
      </nav>

      <div class="wizard-form">
        <!-- Wizard mode: step content -->
        <div v-show="viewMode === 'wizard'" class="wizard-form-wizard">
        <div v-show="currentStepIndex === 0" class="wizard-step">
          <h3 class="wizard-step-title">Upload your resume</h3>
          <p class="wizard-step-helper">We'll use it to prefill the rest of the wizard.</p>
          <div class="form-group form-group-upload">
            <input
              ref="uploadInput"
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              class="wizard-upload-input-hidden"
              @change="onUploadFile"
            />
            <div
              class="wizard-upload-zone"
              :class="{
                'is-dragging': isDragging,
                'is-uploading': uploading,
                'is-uploaded': uploadComplete,
              }"
              @click="triggerUpload"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="onDrop"
            >
              <div class="wizard-upload-zone-inner">
                <div class="wizard-upload-icon-wrap">
                  <i
                    :class="uploadComplete ? 'fas fa-check-circle wizard-upload-icon is-success' : 'fas fa-file-upload wizard-upload-icon'"
                  ></i>
                </div>
                <div class="wizard-upload-text">
                  <template v-if="uploadComplete">
                    <span class="wizard-upload-filename">{{ uploadedLabel }}</span>
                    <span class="wizard-upload-replace">Tap to upload a new file</span>
                  </template>
                  <template v-else>
                    <span class="wizard-upload-click">Click here</span> to upload your file or drag.
                  </template>
                </div>
                <p class="wizard-upload-formats">Supported format: PDF, DOCX, TXT (5MB each)</p>
              </div>
            </div>
            <p v-if="uploadError" class="wizard-message error">{{ uploadError }}</p>
            <p v-if="uploading" class="wizard-step-helper">Uploading and parsing…</p>
          </div>
        </div>

        <div v-show="currentStepIndex === 1" class="wizard-step">
          <h3 class="wizard-step-title">Name</h3>
          <p class="wizard-step-helper">We'll use this on your resume and applications.</p>
          <div class="form-group">
            <label>First Name</label>
            <input v-model="local.firstName" type="text" placeholder="John" />
          </div>
          <div class="form-group">
            <label>Last Name</label>
            <input v-model="local.lastName" type="text" placeholder="Doe" />
          </div>
        </div>

        <div v-show="currentStepIndex === 2" class="wizard-step">
          <h3 class="wizard-step-title">Email</h3>
          <p class="wizard-step-helper">Employers may use this to contact you.</p>
          <div class="form-group">
            <label>Email</label>
            <input v-model="local.email" type="email" placeholder="you@example.com" />
          </div>
        </div>

        <div v-show="currentStepIndex === 3" class="wizard-step">
          <h3 class="wizard-step-title">Phone</h3>
          <p class="wizard-step-helper">Include country code if applying internationally.</p>
          <div class="form-group">
            <label>Phone Number</label>
            <input v-model="local.phone" type="tel" placeholder="+1 (555) 000-0000" />
          </div>
        </div>

        <div v-show="currentStepIndex === 4" class="wizard-step">
          <h3 class="wizard-step-title">Professional headline</h3>
          <p class="wizard-step-helper">A short tagline that appears under your name.</p>
          <div class="form-group">
            <ResumeHeaderEditor v-model="local.headline" />
          </div>
        </div>

        <div v-show="currentStepIndex === 5" class="wizard-step">
          <h3 class="wizard-step-title">Professional summary</h3>
          <p class="wizard-step-helper">Brief overview of your background and goals.</p>
          <div class="form-group">
            <SummaryEditor
              v-model="local.summary"
              v-model:reuse-professional-summary="local.reuseProfessionalSummary"
            />
          </div>
        </div>

        <div v-show="currentStepIndex === 6" class="wizard-step wizard-step-skills">
          <h3 class="wizard-step-title">Skills</h3>
          <p class="wizard-step-helper">Add at least 3 skills. These help tailor your resume to each job.</p>
          <div class="form-group">
            <SkillsEditor v-model="local.skills" />
          </div>
        </div>

        <div v-show="currentStepIndex === 7" class="wizard-step wizard-step-experience">
          <ExperienceEditor v-model="local.experience" />
        </div>

        <div v-show="currentStepIndex === 8" class="wizard-step wizard-step-education">
          <EducationEditor v-model="local.education" />
        </div>
        </div>

        <!-- Preview mode: resume HTML preview -->
        <div v-show="viewMode === 'preview'" class="wizard-form-preview">
          <h3 class="wizard-step-title">Resume preview</h3>
          <p class="wizard-step-helper">Your base resume as employers will see it. Edit details in the wizard when you want to update it.</p>
          <div v-if="previewLoading" class="wizard-preview-loading">
            <i class="fas fa-spinner fa-spin"></i> Loading preview…
          </div>
          <div v-else-if="previewHtml" class="wizard-preview-html" v-html="previewHtml"></div>
          <div v-else class="wizard-preview-placeholder">
            <p>No preview available. Complete the wizard and save to see your resume.</p>
          </div>
        </div>
      </div>
    </div>

    <footer class="wizard-footer">
      <p v-if="saveMessage" :class="['wizard-message', saveSuccess ? 'success' : 'error']">{{ saveMessage }}</p>
      <div
        class="wizard-footer-actions"
        :class="{ 'is-single': currentStepIndex === 0 && viewMode === 'wizard' }"
      >
        <template v-if="viewMode === 'wizard'">
          <button
            v-if="currentStepIndex > 0"
            type="button"
            class="wizard-btn wizard-btn-secondary"
            @click="goBack"
          >
            Back
          </button>
          <button
            v-if="currentStepIndex < steps.length - 1"
            type="button"
            class="wizard-btn wizard-btn-primary wizard-btn-bold"
            @click="goNext"
          >
            {{ primaryButtonLabel }}
          </button>
          <button
            v-else
            type="button"
            class="wizard-btn wizard-btn-primary wizard-btn-bold"
            :disabled="saving || !isDirty"
            @click="saveAll"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </template>
        <template v-else>
          <button type="button" class="wizard-btn wizard-btn-secondary" @click="switchToWizard">
            Back to editor
          </button>
        </template>
      </div>
    </footer>
  </div>
</template>

<script>
import ResumeHeaderEditor from './ResumeHeaderEditor.vue';
import SummaryEditor from './SummaryEditor.vue';
import SkillsEditor from './SkillsEditor.vue';
import ExperienceEditor from './ExperienceEditor.vue';
import EducationEditor from './EducationEditor.vue';
import { updateParsedData, uploadResume } from '@/services/resume';
import axios from '@/axios';

const STEPS = [
  { id: 'upload', label: 'Upload resume' },
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'headline', label: 'Headline' },
  { id: 'summary', label: 'Summary' },
  { id: 'skills', label: 'Skills' },
  { id: 'experience', label: 'Experience' },
  { id: 'education', label: 'Education' },
];

export default {
  name: 'ResumeDetailsWizard',
  components: { ResumeHeaderEditor, SummaryEditor, SkillsEditor, ExperienceEditor, EducationEditor },
  props: {
    form: { type: Object, default: () => ({ firstName: '', lastName: '', email: '', phone: '' }) },
    editForm: { type: Object, default: () => ({ headline: '', summary: '', experience: [], education: [] }) },
    skills: { type: Array, default: () => [] },
    hasBaseResume: { type: Boolean, default: false },
    reuseProfessionalSummary: { type: Boolean, default: false },
  },
  emits: ['saved', 'prefill', 'step-change'],
  data() {
    return {
      steps: STEPS,
      currentStepIndex: 0,
      viewMode: 'wizard', // 'wizard' | 'preview'
      previewHtml: '',
      previewLoading: false,
      local: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        headline: '',
        summary: '',
        reuseProfessionalSummary: false,
        skills: [],
        experience: [],
        education: [],
      },
      saving: false,
      saveMessage: '',
      saveSuccess: false,
      uploading: false,
      uploadError: '',
      uploadedFileName: '',
      isDragging: false,
    };
  },
  watch: {
    /* Sync from parent only when props actually change (e.g. after fetch/save). Avoids recursive updates from deep watch. */
    propsSnapshot() {
      this.syncFromProps();
    },
    currentStepIndex() {
      this.emitStepChange();
    },
  },
  computed: {
    propsSnapshot() {
      return JSON.stringify({
        form: this.form,
        editForm: this.editForm,
        skills: this.skills,
        reuseProfessionalSummary: this.reuseProfessionalSummary,
      });
    },
    hasExistingData() {
      return this.hasBaseResume || this.form?.firstName?.trim() || this.editForm?.headline?.trim() || (this.editForm?.experience?.length > 0);
    },
    stepperTitle() {
      return this.hasExistingData ? 'Resume details' : 'Get started';
    },
    isDirty() {
      const current = JSON.stringify({
        form: {
          firstName: this.local.firstName,
          lastName: this.local.lastName,
          email: this.local.email,
          phone: this.local.phone,
        },
        editForm: {
          headline: this.local.headline,
          summary: this.local.summary,
          experience: this.local.experience,
          education: this.local.education,
        },
        skills: this.local.skills,
        reuseProfessionalSummary: this.local.reuseProfessionalSummary,
      });
      return current !== this.propsSnapshot;
    },
    primaryButtonLabel() {
      return this.isDirty ? 'Save & Continue' : 'Continue';
    },
    uploadComplete() {
      return this.hasBaseResume || !!this.uploadedFileName;
    },
    uploadedLabel() {
      if (this.uploadedFileName) return this.uploadedFileName;
      return 'Resume uploaded';
    },
    /** True when all wizard steps are complete (view preview button and stepper helper shown) */
    wizardValidated() {
      return this.steps.every((step) => this.stepComplete(step));
    },
    mobileProgressPercent() {
      if (!this.steps.length) return 0;
      return Math.round(((this.currentStepIndex + 1) / this.steps.length) * 100);
    },
  },
  mounted() {
    this.syncFromProps();
    this.emitStepChange();
  },
  methods: {
    emitStepChange() {
      this.$emit('step-change', { currentStepIndex: this.currentStepIndex, totalSteps: this.steps.length });
    },
    syncFromProps() {
      this.local = {
        firstName: this.form.firstName || '',
        lastName: this.form.lastName || '',
        email: this.form.email || '',
        phone: this.form.phone || '',
        headline: this.editForm.headline || '',
        summary: this.editForm.summary || '',
        reuseProfessionalSummary: Boolean(this.reuseProfessionalSummary),
        experience: (this.editForm.experience || []).map((e) => ({
          ...e,
          bullets: Array.isArray(e.bullets) ? [...e.bullets] : [],
        })),
        education: (this.editForm.education || []).map((e) => ({ ...e })),
        skills: Array.isArray(this.skills) ? [...this.skills] : [],
      };
      this.saveMessage = '';
    },
    triggerUpload() {
      if (this.uploading) return;
      this.$refs.uploadInput?.click();
    },
    onDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.uploadFile(file);
    },
    async onUploadFile(event) {
      const file = event.target?.files?.[0];
      if (file) this.uploadFile(file);
      event.target.value = '';
    },
    async uploadFile(file) {
      const ext = (file.name || '').toLowerCase().split('.').pop();
      if (!['pdf', 'docx', 'txt'].includes(ext)) {
        this.uploadError = 'Please upload a PDF, DOCX, or TXT file.';
        return;
      }
      this.uploadError = '';
      this.uploading = true;
      try {
        const data = await uploadResume(file);
        this.uploadedFileName = file.name || 'Resume uploaded';
        if (data.extractedData) {
          this.$emit('prefill', data.extractedData);
        }
      } catch (err) {
        this.uploadError = err?.response?.data?.message || err?.message || 'Upload failed.';
      } finally {
        this.uploading = false;
        if (this.$refs.uploadInput) this.$refs.uploadInput.value = '';
      }
    },
    stepComplete(step) {
      switch (step.id) {
        case 'upload':
          return this.hasBaseResume;
        case 'name':
          return !!(this.local.firstName?.trim() && this.local.lastName?.trim());
        case 'email':
          return !!this.local.email?.trim();
        case 'phone':
          return this.isPhoneValid(this.local.phone);
        case 'headline':
          return true; // optional
        case 'summary':
          return true; // optional
        case 'skills':
          return Array.isArray(this.local.skills) && this.local.skills.length >= 3;
        case 'experience':
          return (
            Array.isArray(this.local.experience) &&
            this.local.experience.length > 0 &&
            this.local.experience.every(
              (e) => e.title?.trim() && e.company?.trim() && Array.isArray(e.bullets) && e.bullets.length > 0
            )
          );
        case 'education':
          return (
            Array.isArray(this.local.education) &&
            this.local.education.length > 0 &&
            this.local.education.every((e) => e.degree?.trim() && e.institution?.trim())
          );
        default:
          return false;
      }
    },
    isPhoneValid(phone) {
      const s = (phone || '').trim();
      if (!s) return false;
      const digits = s.replace(/\D/g, '');
      return digits.length >= 3;
    },
    goToStep(index) {
      this.viewMode = 'wizard';
      this.currentStepIndex = index;
    },
    goBack() {
      if (this.currentStepIndex > 0) this.currentStepIndex--;
    },
    async switchToPreview() {
      if (!this.hasBaseResume) return;
      this.viewMode = 'preview';
      await this.fetchPreview();
    },
    switchToWizard() {
      this.viewMode = 'wizard';
    },
    async fetchPreview() {
      this.previewLoading = true;
      this.previewHtml = '';
      try {
        const { data } = await axios.get('/api/resume/preview');
        if (data.html) this.previewHtml = data.html;
      } catch (err) {
        this.previewHtml = '';
      } finally {
        this.previewLoading = false;
      }
    },
    goNext() {
      if (this.currentStepIndex < this.steps.length - 1) this.currentStepIndex++;
    },
    async saveAll() {
      const { experience, education, skills } = this.local;
      if (!Array.isArray(skills) || skills.length < 3) {
        this.saveMessage = 'Please add at least 3 skills.';
        this.saveSuccess = false;
        return;
      }
      for (const exp of experience) {
        if (!exp.title?.trim() || !exp.company?.trim()) {
          this.saveMessage = 'Each experience must have a title and company.';
          this.saveSuccess = false;
          return;
        }
        if (!exp.bullets?.length) {
          this.saveMessage = 'Each experience must have at least one bullet.';
          this.saveSuccess = false;
          return;
        }
      }
      for (const edu of education) {
        if (!edu.degree?.trim() || !edu.institution?.trim()) {
          this.saveMessage = 'Each education entry must have degree and institution.';
          this.saveSuccess = false;
          return;
        }
      }

      this.saving = true;
      this.saveMessage = '';
      try {
        await axios.put('/api/auth/profile', {
          firstName: this.local.firstName,
          lastName: this.local.lastName,
          email: this.local.email,
          phone: this.local.phone,
          skills: this.local.skills,
        });
        await updateParsedData({
          headline: this.local.headline || undefined,
          summary: this.local.summary || undefined,
          experience: this.local.experience,
          education: this.local.education,
          skills: this.local.skills,
          reuseProfessionalSummary: Boolean(this.local.reuseProfessionalSummary),
        });
        if (this.$store?.state?.auth?.user) {
          this.$store.commit('auth/SET_AUTH', {
            user: {
              ...this.$store.state.auth.user,
              firstName: this.local.firstName,
              lastName: this.local.lastName,
              email: this.local.email,
              phone: this.local.phone,
              skills: this.local.skills,
            },
            token: localStorage.getItem('token'),
          });
        }
        this.saveMessage = 'All changes saved.';
        this.saveSuccess = true;
        this.$emit('saved', {
          form: {
            firstName: this.local.firstName,
            lastName: this.local.lastName,
            email: this.local.email,
            phone: this.local.phone,
            skills: this.local.skills,
          },
          editForm: {
            headline: this.local.headline,
            summary: this.local.summary,
            experience: this.local.experience,
            education: this.local.education,
          },
          reuseProfessionalSummary: Boolean(this.local.reuseProfessionalSummary),
        });
        if (this.viewMode === 'preview') await this.fetchPreview();
      } catch (err) {
        this.saveMessage = err?.response?.data?.message || err?.message || 'Failed to save.';
        this.saveSuccess = false;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* All-white onboarding layout: no panel borders, calm and borderless */
.wizard-fullpage {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  margin: 0;
}

.wizard-fullpage.wizard-onboarding {
  background: var(--app-surface);
  color: var(--app-text);
}

.wizard-main {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  align-items: flex-start;
  padding: 0;
  margin: 0;
}

.wizard-onboarding .wizard-main {
  background: var(--app-surface);
}

/* Shared top padding so "Resume details" and step title (e.g. "Upload your resume") align on the same baseline */
.wizard-main {
  --wizard-content-top: 24px;
}

/* Stepper: sticky, full height of wizard row; same top padding as form so titles align */
.wizard-stepper {
  position: sticky;
  top: var(--app-header-height, 64px);
  align-self: stretch;
  height: 100%;
  width: 280px;
  min-width: 260px;
  max-width: 300px;
  flex-shrink: 0;
  padding: var(--wizard-content-top, 24px) 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
  z-index: 10;
  min-height: 0;
  overflow-y: auto;
}

.wizard-onboarding .wizard-stepper {
  background: var(--app-surface);
  border-right: none;
}

.wizard-stepper-header {
  padding-bottom: 16px;
  margin-bottom: 12px;
}

.wizard-onboarding .wizard-stepper-header {
  border-bottom: none;
}

.wizard-stepper-title {
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 700;
}

.wizard-onboarding .wizard-stepper-title {
  color: var(--app-text);
}

.wizard-stepper-progress {
  margin: 0;
  font-size: 13px;
}

.wizard-onboarding .wizard-stepper-progress {
  color: var(--text-muted);
}

.wizard-stepper-steps {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.wizard-stepper-helper {
  margin: 16px 0 0;
  padding: 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-muted);
}

.wizard-onboarding .wizard-stepper-helper {
  color: var(--text-muted);
}

.wizard-stepper-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: none;
  border: none;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  width: 100%;
  border-radius: 8px;
  transition: background 0.2s, color 0.2s;
  color: var(--step-text-default, var(--app-text-secondary));
  -webkit-appearance: none;
  appearance: none;
}

.wizard-stepper-item:hover {
  background: var(--app-surface-hover);
  color: var(--step-text-active, var(--app-text));
}

.wizard-stepper-item.is-complete {
  color: var(--step-text-active, var(--app-text));
}

.wizard-stepper-item.is-pending {
  color: var(--step-text-default, var(--text-muted));
}

.wizard-stepper-item.is-complete .wizard-stepper-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.wizard-onboarding .wizard-stepper-item.is-complete .wizard-stepper-icon {
  background: var(--app-primary);
  color: var(--app-primary-text);
}

.wizard-stepper-item.is-current {
  font-weight: 600;
  background: var(--app-surface-hover);
  border-left: 3px solid var(--app-primary);
  padding-left: 12px;
  margin-left: 0;
  padding-right: 12px;
  margin-right: 0;
}

.wizard-stepper-item.is-current {
  color: var(--step-text-active, var(--app-text));
}

.wizard-stepper-item.is-current .wizard-stepper-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--app-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
  color: var(--app-primary);
}

.wizard-stepper-item.is-pending .wizard-stepper-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.wizard-onboarding .wizard-stepper-item.is-pending .wizard-stepper-number {
  background: var(--app-border);
  color: var(--text-muted);
}

.wizard-stepper-label {
  flex: 1;
  min-width: 0;
  color: inherit;
}

/* Mobile: compact progress strip (full step list is desktop-only) */
.wizard-stepper-mobile {
  display: none;
}

.wizard-mobile-progress {
  height: 4px;
  background: var(--app-surface-hover);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}

.wizard-mobile-progress-fill {
  height: 100%;
  background: var(--app-primary);
  border-radius: 4px;
  transition: width 0.25s ease;
}

.wizard-mobile-current-step {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--app-text);
}

.wizard-mobile-dots {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.wizard-mobile-dots::-webkit-scrollbar {
  display: none;
}

.wizard-mobile-dot {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--app-border);
  background: var(--app-surface);
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.wizard-mobile-dot.is-complete {
  background: var(--app-primary);
  border-color: var(--app-primary);
  color: var(--app-primary-text);
}

.wizard-mobile-dot.is-complete i {
  font-size: 10px;
}

.wizard-mobile-dot.is-current {
  border-color: var(--app-primary);
  color: var(--app-primary);
  box-shadow: none;
}

/* Form: white, no border – no horizontal scroll; extra bottom padding so content isn’t hidden under fixed footer */
.wizard-form {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: var(--wizard-content-top, 24px) 40px;
  padding-bottom: calc(
    32px + var(--wizard-footer-height, 72px) + var(--wizard-footer-bottom, 0px) + var(--wizard-safe-bottom, 0px)
  );
  min-width: 0;
  max-width: 100%;
}

.wizard-onboarding .wizard-form {
  background: var(--app-surface);
}

.wizard-form-wizard {
  min-width: 0;
  max-width: 100%;
}

.wizard-form-preview {
  min-width: 0;
  max-width: 100%;
}

.wizard-preview-loading {
  padding: 24px;
  text-align: center;
  color: var(--text-muted);
}

.wizard-preview-html {
  padding: 16px 0;
  background: var(--app-surface);
  border-radius: 8px;
  overflow-x: auto;
}

.wizard-onboarding .wizard-preview-html {
  background: var(--app-surface-hover);
}

.wizard-preview-placeholder {
  padding: 32px 24px;
  text-align: center;
  color: var(--text-muted);
  background: var(--app-surface-hover);
  border-radius: 8px;
}

.wizard-step-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--app-text);
}

.wizard-step {
  min-width: 0;
  max-width: 100%;
}

.wizard-step-helper {
  margin: 0 0 24px;
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.5;
}

.wizard-step-experience .wizard-step-helper {
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 24px;
  min-width: 0;
  max-width: 100%;
}

.form-group-upload {
  width: 100%;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  padding: 0;
  box-sizing: border-box;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
}

.wizard-onboarding .form-group label {
  color: var(--app-text);
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  border-radius: 8px;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.wizard-onboarding .form-group input {
  color: var(--text-primary);
  background: #ffffff;
  border: 1px solid var(--app-border);
}

.form-group input:hover:not(:disabled) {
  border-color: var(--input-border-hover);
}

.form-group input::placeholder {
  color: var(--text-muted);
  opacity: 1;
}

.wizard-onboarding .form-group input::placeholder {
  color: var(--text-muted);
  opacity: 1;
}

.form-group input:focus {
  outline: none;
  border-color: var(--app-primary);
  box-shadow: 0 0 0 2px rgba(91, 33, 182, 0.2);
}

.form-group input:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.wizard-onboarding .form-group input:disabled {
  background: var(--app-surface-hover);
}

/* Footer: fixed at bottom of viewport (like the header) so it’s always visible */
.wizard-footer {
  position: fixed;
  bottom: var(--wizard-footer-bottom, 0);
  left: var(--wizard-footer-left, 0);
  right: 0;
  min-height: var(--wizard-footer-height, 72px);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 32px;
  z-index: 101;
  border-radius: 0;
  flex-shrink: 0;
}

.wizard-onboarding .wizard-footer {
  background: var(--app-surface);
  border-top: 1px solid var(--app-border);
  box-shadow: none;
}

.wizard-message {
  margin: 0;
  margin-right: auto;
  font-size: 14px;
}

.wizard-message.success {
  color: #16a34a;
}

.wizard-message.error {
  color: var(--app-error);
}

.wizard-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.wizard-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  transition: background 0.2s, color 0.2s;
}

.wizard-btn-secondary {
  background: var(--app-surface);
  color: var(--app-text);
  border: 1px solid var(--app-border);
}

.wizard-onboarding .wizard-btn-secondary:hover:not(:disabled) {
  background: var(--app-surface-hover);
}

.wizard-btn-secondary:disabled {
  opacity: 1;
  color: var(--text-muted);
  background: var(--app-surface-hover);
  border-color: var(--app-border);
  cursor: not-allowed;
}

.wizard-btn-primary {
  background: var(--app-primary);
  color: var(--app-primary-text);
}

.wizard-btn-primary:hover:not(:disabled) {
  background: var(--app-primary-hover);
}

.wizard-btn-bold {
  font-weight: 700;
}

.wizard-btn-outline {
  background: transparent;
  color: var(--app-primary);
  border: 1px solid var(--app-primary);
}

.wizard-btn-outline:hover:not(:disabled) {
  background: color-mix(in srgb, var(--app-primary) 10%, transparent);
}

.wizard-upload-input-hidden {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.wizard-upload-zone {
  display: block;
  width: 100%;
  max-width: none;
  min-height: 120px;
  margin: 0;
  padding: 24px 16px;
  box-sizing: border-box;
  background: #ffffff;
  border: 2px dashed color-mix(in srgb, var(--app-primary) 40%, var(--app-border));
  border-radius: 12px;
  box-shadow: none;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.wizard-upload-zone:hover {
  border-color: var(--app-primary);
  box-shadow: none;
}

.wizard-upload-zone.is-dragging {
  border-color: var(--app-primary-hover);
  background: var(--surface-3, #f2f2f7);
  box-shadow: none;
}

.wizard-upload-zone.is-uploading {
  pointer-events: none;
  opacity: 0.85;
}

.wizard-upload-zone.is-uploaded {
  border-style: solid;
  border-color: color-mix(in srgb, var(--app-primary) 35%, var(--app-border));
  background: #ffffff;
}

.wizard-upload-zone-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.wizard-upload-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--surface-3, #f2f2f7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wizard-upload-icon {
  font-size: 22px;
  color: var(--app-primary);
}

.wizard-upload-icon.is-success {
  color: #16a34a;
}

.wizard-upload-text {
  font-size: 15px;
  color: var(--app-text);
  text-align: center;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wizard-upload-filename {
  font-weight: 600;
  color: var(--app-text);
  word-break: break-word;
}

.wizard-upload-replace {
  font-size: 13px;
  color: var(--app-primary);
  font-weight: 500;
}

.wizard-upload-click {
  font-weight: 700;
  color: var(--app-primary);
  text-decoration: underline;
}

.wizard-upload-formats {
  width: 100%;
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
}

.wizard-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Child editors – white mode overrides when inside onboarding */
.wizard-onboarding .wizard-form :deep(.label),
.wizard-onboarding .wizard-form :deep(.block .label) {
  color: var(--app-text);
}

.wizard-onboarding .wizard-form :deep(.input),
.wizard-onboarding .wizard-form :deep(.textarea),
.wizard-onboarding .wizard-form :deep(input:not([type="file"]):not([type="checkbox"]):not([type="radio"])),
.wizard-onboarding .wizard-form :deep(textarea),
.wizard-onboarding .wizard-form :deep(select) {
  background: #ffffff;
  border: 1px solid var(--app-border);
  color: var(--text-primary);
}

.wizard-onboarding .wizard-form :deep(.input:hover:not(:disabled)),
.wizard-onboarding .wizard-form :deep(.textarea:hover:not(:disabled)),
.wizard-onboarding .wizard-form :deep(input:hover:not(:disabled)),
.wizard-onboarding .wizard-form :deep(textarea:hover:not(:disabled)) {
  border-color: var(--input-border-hover);
}

.wizard-onboarding .wizard-form :deep(.input:focus),
.wizard-onboarding .wizard-form :deep(.textarea:focus),
.wizard-onboarding .wizard-form :deep(input:focus),
.wizard-onboarding .wizard-form :deep(textarea:focus) {
  outline: none;
  border-color: var(--app-primary);
  box-shadow: 0 0 0 2px rgba(91, 33, 182, 0.2);
}

.wizard-onboarding .wizard-form :deep(.input::placeholder),
.wizard-onboarding .wizard-form :deep(.textarea::placeholder),
.wizard-onboarding .wizard-form :deep(input::placeholder),
.wizard-onboarding .wizard-form :deep(textarea::placeholder) {
  color: var(--text-muted);
  opacity: 1;
}

.wizard-onboarding .wizard-form :deep(input:disabled),
.wizard-onboarding .wizard-form :deep(textarea:disabled) {
  background: var(--app-surface-hover);
  opacity: 0.8;
  cursor: not-allowed;
}

.wizard-onboarding .wizard-form :deep(.card) {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
}

.wizard-onboarding .wizard-form :deep(.btn-sm),
.wizard-onboarding .wizard-form :deep(.btn) {
  background: var(--app-surface);
  color: var(--app-text);
  border: 1px solid var(--app-border);
}

.wizard-onboarding .wizard-form :deep(.btn.remove) {
  color: var(--app-error);
  border-color: var(--app-error);
}

.wizard-onboarding .wizard-form :deep(.remove-modal) {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

@media (max-width: 768px) {
  .wizard-main {
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .wizard-stepper {
    width: 100%;
    min-width: 0;
    max-width: none;
    flex-direction: column;
    border-right: none;
    padding: 10px var(--dashboard-inline-pad, 12px) 8px;
    position: static;
    height: auto;
    max-height: none;
    overflow: visible;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .wizard-onboarding .wizard-stepper {
    border-bottom: 1px solid var(--app-border);
  }

  .wizard-stepper-header {
    width: 100%;
    margin-bottom: 6px;
    padding-bottom: 0;
  }

  .wizard-stepper-title {
    font-size: 0.9rem;
  }

  .wizard-stepper-progress {
    font-size: 12px;
  }

  .wizard-stepper-mobile {
    display: block;
  }

  .wizard-stepper-steps-desktop {
    display: none;
  }

  .wizard-stepper-helper-desktop {
    display: none;
  }

  .wizard-form {
    flex: 1;
    min-height: 0;
    padding: 12px var(--dashboard-inline-pad, 12px);
    padding-bottom: calc(
      24px + var(--wizard-footer-height, 64px) + var(--wizard-footer-bottom, 0px) + var(--wizard-safe-bottom, 0px)
    );
    box-sizing: border-box;
    width: 100%;
    max-width: none;
  }

  .wizard-step,
  .wizard-form-wizard,
  .form-group-upload {
    width: 100%;
    max-width: none;
    box-sizing: border-box;
  }

  .wizard-upload-zone {
    width: 100%;
    max-width: none;
    margin: 0 0 8px;
    padding: 24px 16px;
    min-height: 120px;
  }

  .wizard-step-title {
    font-size: 1.1rem;
  }

  .wizard-step-helper {
    font-size: 13px;
    margin-bottom: 16px;
  }

  .wizard-mobile-progress {
    margin-bottom: 8px;
  }

  .wizard-mobile-current-step {
    display: none; /* section title already names the step */
  }

  .wizard-mobile-dots {
    display: flex;
    gap: 4px;
    justify-content: flex-start;
    overflow-x: auto;
    max-width: 100%;
    padding-right: 2px;
    -webkit-overflow-scrolling: touch;
  }

  .wizard-mobile-dot {
    width: 22px;
    height: 22px;
    font-size: 10px;
    flex-shrink: 0;
  }

  .wizard-upload-zone-inner {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }

  .wizard-footer {
    padding: 8px var(--dashboard-inline-pad, 12px);
    flex-wrap: nowrap;
    align-items: stretch;
    gap: 8px;
    z-index: 90;
    min-height: 0;
    height: auto;
    box-sizing: border-box;
  }

  .wizard-footer-actions {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
  }

  .wizard-footer-actions .wizard-btn {
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    padding: 12px 8px;
    font-size: 14px;
    text-align: center;
    min-height: var(--control-height, 44px);
  }

  .wizard-footer-actions.is-single .wizard-btn-primary {
    flex: 1 1 100%;
  }

  .wizard-message {
    display: none;
  }
}
</style>
